import type { ApiResponse, ListAffiliateUsersResponse } from '@fluenceur/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import type { AffiliateFilters } from '@/types/filters';

// Extend the base filters with programId for this specific query
interface SearchAffiliatesQueryFilters extends AffiliateFilters {
  programId?: string;
}

export const fetchAffiliateList = async (
  filters: SearchAffiliatesQueryFilters = {}
): Promise<ListAffiliateUsersResponse> => {
  // Build query parameters matching backend expectations
  const params = new URLSearchParams();

  // Always include pagination
  params.append('page', (filters.page || 1).toString());
  params.append('per_page', (filters.perPage || 20).toString());

  // Include program_id if provided
  if (filters.programId) {
    params.append('program_id', filters.programId);
  }

  // Include other filters if they exist
  if (filters.search && filters.search.length > 1) {
    params.append('search', filters.search);
  }

  if (filters.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }

  if (filters.sortBy) {
    params.append('sort_by', filters.sortBy);
  }

  if (filters.sortDirection) {
    params.append('sort_direction', filters.sortDirection);
  }

  if (filters.dateFilterType) {
    params.append('date_filter_type', filters.dateFilterType);
  }

  if (filters.startDate) {
    params.append('start_date', filters.startDate);
  }

  if (filters.endDate) {
    params.append('end_date', filters.endDate);
  }

  if (filters.source) {
    params.append('source', filters.source);
  }

  if (filters.importBatchId) {
    params.append('import_batch_id', filters.importBatchId);
  }

  const { data } = await api.get<ApiResponse<ListAffiliateUsersResponse>>(
    `/dashboard/affiliates?${params.toString()}`
  );

  if (!data.data) {
    throw new Error('Invalid response structure');
  }

  return data.data;
};

export const useSearchAffiliatesQuery = (
  filters: SearchAffiliatesQueryFilters = {},
  enabled = true
) => {
  return useQuery<ListAffiliateUsersResponse, Error>({
    queryKey: [
      'affiliates',
      'list',
      filters.page || 1,
      filters.perPage || 20,
      filters.search,
      filters.status,
      filters.sortBy,
      filters.sortDirection,
      filters.dateFilterType,
      filters.startDate,
      filters.endDate,
      filters.programId,
      filters.source,
      filters.importBatchId,
    ],
    queryFn: () => fetchAffiliateList(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
    enabled,
  });
};
